// FILE: src/services/journeyMapService.js
//
// Derives the 7-node Journey Roadmap state from existing learner data —
// readiness, sessions, baseline, achievements. Pure; no IO.
//
// Nodes (left → right):
//   baseline  → practice → targeted → full_sim → ready → three_sims → certified
//
// Each node has one of three states:
//   - 'earned'   — completed; the visual shows full color + check badge
//   - 'current'  — the learner's active step; visual lifts + glows
//   - 'upcoming' — not yet started; faded
//
// The "current" node is the first non-earned node, which gives the user a
// clear "you are here" without having to compute it client-side.

export const ROADMAP_NODES = [
  { key: 'baseline',    label: 'Baseline',     asset: 'roadmap_baseline.png'   },
  { key: 'practice',    label: 'Practice',     asset: 'roadmap_practice.png'   },
  { key: 'targeted',    label: 'Targeted Work',asset: 'roadmap_targeted.png'   },
  { key: 'full_sim',    label: 'Full Sim 1',   asset: 'roadmap_sim.png'        },
  { key: 'ready',       label: 'Ready 75+',    asset: 'roadmap_ready.png'      },
  { key: 'three_sims',  label: 'Three Sims',   asset: 'roadmap_three_sims.png' },
  { key: 'certified',   label: 'Certified',    asset: 'roadmap_certified.png'  },
];

/**
 * Derive each node's earned/current state.
 *
 * @param {object} params
 * @param {object|null} params.readiness     — readiness object from computeReadiness()
 * @param {Array}       params.sessions      — newest-first session rows
 * @param {string|null} params.baselineAt    — ISO baseline date for activeCert
 * @param {object}      params.earnedByKey   — keyed map of earned achievements
 *                                             (achievement_key → row)
 * @returns {Array<{ key, label, asset, state, subtitle }>}
 */
export function deriveJourneyState({ readiness, sessions, baselineAt, earnedByKey = {} }) {
  const fullSimCount = countFullSims(sessions);
  const score = typeof readiness?.score === 'number' ? readiness.score : 0;
  const certified = !!earnedByKey?.certification_earned;

  // Earned predicates per node — most foundational first.
  const earned = {
    baseline:   !!baselineAt,
    practice:   sessions.length >= 1,
    targeted:   sessions.length >= 3,
    full_sim:   fullSimCount >= 1,
    ready:      score >= 75,
    three_sims: fullSimCount >= 3,
    certified:  certified,
  };

  // First non-earned node becomes the current step. If everything's earned,
  // 'certified' stays earned (no current marker).
  let currentKey = null;
  for (const n of ROADMAP_NODES) {
    if (!earned[n.key]) { currentKey = n.key; break; }
  }

  return ROADMAP_NODES.map((n) => {
    let state = 'upcoming';
    if (earned[n.key]) state = 'earned';
    else if (n.key === currentKey) state = 'current';
    return {
      ...n,
      state,
      subtitle: subtitleFor(n.key, state, { baselineAt, fullSimCount, score }),
    };
  });
}

function countFullSims(sessions) {
  if (!Array.isArray(sessions)) return 0;
  let n = 0;
  for (const s of sessions) {
    const mode = (s?.mode || '').toLowerCase();
    if (mode === 'full_sim' || mode === 'fullsim' || mode === 'full-sim' || mode === 'sim') n += 1;
  }
  return n;
}

function subtitleFor(key, state, ctx) {
  if (state === 'earned') {
    if (key === 'baseline' && ctx.baselineAt) {
      const d = new Date(ctx.baselineAt);
      if (Number.isFinite(d.getTime())) {
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
    }
    if (key === 'full_sim' || key === 'three_sims') {
      return `${ctx.fullSimCount} done`;
    }
    if (key === 'ready') return `${Math.round(ctx.score)}/100`;
    if (key === 'certified') return 'Goal reached';
    return 'Done';
  }
  if (state === 'current') return 'Current step';
  if (key === 'certified') return 'Goal';
  return 'Upcoming';
}
